import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Ticket, User, LogOut, LayoutDashboard, Menu, X, Sparkles, Settings, Shield, Tag, Heart, Users, UserPlus } from 'lucide-react';
import { useState } from 'react';
import LanguageSwitcher from './LanguageSwitcher';

export default function Layout() {
  const { user, logout, isAuthenticated, isInfluencer, isAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="FanBound" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/events" 
                className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                data-testid="nav-browse-events"
              >
                {t('nav.browseEvents')}
              </Link>
              <Link 
                to="/resale" 
                className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                data-testid="nav-resale"
              >
                {t('nav.resaleMarket')}
              </Link>
              {isAuthenticated && (
                <Link 
                  to="/my-tickets" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  data-testid="nav-my-tickets"
                >
                  {t('nav.myTickets')}
                </Link>
              )}
              {isAuthenticated && (
                <Link 
                  to="/following" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  data-testid="nav-following"
                >
                  Following
                </Link>
              )}
              {isAuthenticated && (
                <Link 
                  to="/liked-events" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  data-testid="nav-liked-events"
                >
                  Liked
                </Link>
              )}
              {isInfluencer && (
                <Link 
                  to="/dashboard" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Link>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  data-testid="nav-admin"
                >
                  {t('nav.admin')}
                </Link>
              )}
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* Prominent Influencer Signup - Always visible */}
              {!isInfluencer && (
                <Link to="/influencer-signup" data-testid="nav-influencer-signup">
                  <Button 
                    variant="outline" 
                    className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary font-semibold gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('nav.becomeCreator')}
                  </Button>
                </Link>
              )}

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <span className="max-w-[100px] truncate">{user?.display_name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/my-tickets')} data-testid="menu-my-tickets">
                      <Ticket className="w-4 h-4 mr-2" />
                      {t('nav.myTickets')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/following')} data-testid="menu-following">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Following
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/liked-events')} data-testid="menu-liked">
                      <Heart className="w-4 h-4 mr-2" />
                      Liked Events
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/team-dashboard')} data-testid="menu-teams">
                      <Users className="w-4 h-4 mr-2" />
                      Teams
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/resale')} data-testid="menu-resale">
                      <Tag className="w-4 h-4 mr-2" />
                      {t('nav.resaleMarket')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {isInfluencer && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        {t('nav.dashboard')}
                      </DropdownMenuItem>
                    )}
                    {isInfluencer && (
                      <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                        <Settings className="w-4 h-4 mr-2" />
                        {t('nav.profileSettings')}
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2" />
                        {t('nav.admin')}
                      </DropdownMenuItem>
                    )}
                    {(isInfluencer || isAdmin) && <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('common.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login">
                    <Button variant="ghost">{t('common.login')}</Button>
                  </Link>
                  <Link to="/signup" data-testid="nav-signup">
                    <Button>{t('common.signup')}</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-background/95 backdrop-blur-lg">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/events" 
                className="block py-2 text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse Events
              </Link>
              <Link 
                to="/resale" 
                className="block py-2 text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Resale Market
              </Link>
              {isAuthenticated && (
                <>
                  <Link 
                    to="/my-tickets" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Tickets
                  </Link>
                  <Link 
                    to="/following" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Following
                  </Link>
                  <Link 
                    to="/liked-events" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Liked Events
                  </Link>
                  <Link 
                    to="/team-dashboard" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Teams
                  </Link>
                </>
              )}
              {isInfluencer && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Creator Dashboard
                  </Link>
                  <Link 
                    to="/settings" 
                    className="block py-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile Settings
                  </Link>
                </>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="block py-2 text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin Panel
                </Link>
              )}
              
              <div className="pt-3 border-t border-white/10 space-y-3">
                {/* Mobile Influencer Signup */}
                {!isInfluencer && (
                  <Link to="/influencer-signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full gap-2 bg-primary/20 border border-primary text-primary hover:bg-primary hover:text-white">
                      <Sparkles className="w-4 h-4" />
                      Become a Creator
                    </Button>
                  </Link>
                )}
                
                {isAuthenticated ? (
                  <Button variant="outline" className="w-full" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                    Log Out
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Log In</Button>
                    </Link>
                    <Link to="/signup" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full">Sign Up</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-card mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="FanBound" className="h-10 w-auto" />
              </div>
              <p className="text-sm text-muted-foreground">
                Connect with your favorite influencers through exclusive events.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">For Fans</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/events" className="hover:text-foreground transition-colors">Browse Events</Link></li>
                <li><Link to="/signup" className="hover:text-foreground transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">For Creators</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/influencer-signup" className="hover:text-foreground transition-colors">Become a Creator</Link></li>
                <li><Link to="/dashboard" className="hover:text-foreground transition-colors">Creator Dashboard</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">For Agencies</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/agency/signup" className="hover:text-foreground transition-colors">Agency Partner Program</Link></li>
                <li><Link to="/agency/login" className="hover:text-foreground transition-colors">Agency Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-foreground transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2026 FanBound. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
